import { IdentityAvatar } from '@/heroui/IdentityAvatar';
import { useCallback, useEffect, useState, type ComponentType, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@heroui/react';
import { Tooltip } from '@/components/Tooltip';
import { ChatBubbleIcon, SearchIcon, UsersIcon, PlusIcon, LinkIcon, SettingsIcon } from '@/components/icons';
import { HeroFadeIn } from '@/heroui/motion/HeroFadeIn';
import { BunkerLogo } from '@/components/BunkerLogo';
import { emitAppEvent } from '@/lib/appEvents';
import { isPlatformEmbedded } from '@/lib/platformEmbed';
import { useAuthStore } from '@/stores/authStore';
import { useDataStore } from '@/stores/dataStore';
import { api } from '@/lib/api';
import { toast } from '@/lib/toast';

type SearchUser = Awaited<ReturnType<typeof api.searchUsers>>['users'][number];

/**
 * Empty content-pane placeholder for the HeroUI shell.
 *
 * In a guild: a quiet "pick a channel" hint. On the DM home it's a real
 * launch-point — an always-open "card nav" panel (ReactBits-style): a top bar (mark + greeting + «Настройки»)
 * over three cards — find people, start a group, servers.
 */
export function HeroEmptyState({ inGuild }: { inGuild: boolean }) {
  const { t } = useTranslation();
  const me = useAuthStore((s) => s.user);
  // Card-nav collapse — the cards can fold into just the bar (ReactBits-style),
  // but start open so the launch actions are there on first paint.
  const [collapsed, setCollapsed] = useState(false);

  if (inGuild) {
    return (
      <div className="grid h-full place-items-center p-6">
        <HeroFadeIn className="flex max-w-sm flex-col items-center gap-3 text-center">
          <div className="grid size-16 place-items-center rounded-xl bg-default text-foreground/70">
            <ChatBubbleIcon className="size-8" />
          </div>
          <h2 className="text-2xl font-semibold tracking-tight text-foreground">{t('home.pickChannel')}</h2>
        </HeroFadeIn>
      </div>
    );
  }

  const name = me?.displayName?.trim() || me?.username || '';
  return (
    <div className="studio-home relative flex h-full min-w-0 items-center justify-center overflow-y-auto overflow-x-hidden p-4 sm:p-6">
      <HeroFadeIn className="relative z-10 my-auto w-full min-w-0 max-w-3xl">
        <div className="studio-home-shell min-w-0 overflow-hidden rounded-3xl bg-[rgb(var(--surface-card)/0.82)] shadow-2xl backdrop-blur-xl">
          {/* Top bar — brand mark + greeting on the left, Bunker's settings
              (voice, filters, notifications) on the right. The profile is the
              shell's when there is one, so the button never opens it. */}
          <div className="studio-home-head flex min-w-0 items-center justify-between gap-3 px-4 py-3.5 sm:px-5">
            <div className="flex min-w-0 items-center gap-3.5">
              {/* Inside the platform frame the brand is the shell's. */}
              {!isPlatformEmbedded() && <BunkerLogo className="h-8 w-auto shrink-0 text-foreground sm:h-10" />}
              <div className="min-w-0">
                <div className="truncate text-lg font-semibold tracking-tight text-foreground sm:text-xl">
                  {name ? t('home.greeting', { name }) : t('home.greetingNoName')}
                </div>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <Button
                size="sm"
                variant="primary"
                aria-label={t('home.settingsButton')}
                onPress={() => emitAppEvent('settings:open', {})}
              >
                <span className="flex items-center gap-1.5">
                  <SettingsIcon className="size-4" />
                  <span className="hidden sm:inline">{t('home.settingsButton')}</span>
                </span>
              </Button>
              {/* Fold the cards away (ReactBits card-nav) — open by default. */}
              <Tooltip label={t(collapsed ? 'home.expandCards' : 'home.collapseCards')}>
              <button
                type="button"
                onClick={() => setCollapsed((c) => !c)}
                aria-label={t(collapsed ? 'home.expandCards' : 'home.collapseCards')}
                aria-expanded={!collapsed}
                className="grid size-11 shrink-0 place-items-center rounded-lg text-foreground/55 transition-colors hover:bg-foreground/[0.07] hover:text-foreground"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={'size-5 transition-transform duration-200 ' + (collapsed ? '' : 'rotate-180')}
                  aria-hidden
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
              </Tooltip>
            </div>
          </div>
          {/* Cards — collapsible; the grid animates its height open/closed. */}
          <div
            className={
              'grid transition-[transform,background-color,border-color,color,box-shadow,opacity] duration-300 ease-out ' +
              (collapsed ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] opacity-100')
            }
          >
            <div className="overflow-hidden">
              <div className="studio-home-grid grid gap-2 p-3 sm:grid-cols-2 sm:gap-3 sm:p-4 lg:grid-cols-3">
                <PeopleCard />
                <GroupCard />
                <ServersCard />
              </div>
            </div>
          </div>
        </div>
      </HeroFadeIn>
    </div>
  );
}

/** Card 1 — find people by @handle or ID and open a DM, inline. */
function PeopleCard() {
  const { t } = useTranslation();
  const upsertDmChannel = useDataStore((s) => s.upsertDmChannel);
  const selectDirectChannel = useDataStore((s) => s.selectDirectChannel);
  const [q, setQ] = useState('');
  const [results, setResults] = useState<SearchUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [opening, setOpening] = useState<string | null>(null);

  useEffect(() => {
    const query = q.trim();
    if (!query) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    // Abort the previous request on each new query (and on unmount) so a slow
    // earlier response can't overwrite a newer one — clearing the timeout only
    // stops requests that haven't fired yet, not one already on the network.
    const ctrl = new AbortController();
    const id = window.setTimeout(async () => {
      try {
        const r = await api.searchUsers(query, undefined, ctrl.signal);
        setResults(Array.isArray(r?.users) ? r.users : []);
      } catch (err) {
        if ((err as { name?: string })?.name === 'AbortError') return;
        console.warn('people search failed', err);
        setResults([]);
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    }, 250);
    return () => {
      ctrl.abort();
      window.clearTimeout(id);
    };
  }, [q]);

  const openDm = useCallback(
    async (user: SearchUser) => {
      if (opening) return;
      setOpening(user.id);
      try {
        const channel = await api.openDirectMessage(user.id);
        upsertDmChannel(channel);
        selectDirectChannel(channel.id);
      } catch {
        toast.error(t('home.findPeopleFailed'));
      } finally {
        setOpening(null);
      }
    },
    [opening, upsertDmChannel, selectDirectChannel, t],
  );

  return (
    <Card title={t('home.findPeople')} subtitle={t('home.findPeopleSub')}>
      <label className="mt-1 flex items-center gap-2 rounded-lg  bg-foreground/[0.05] px-2.5 py-2 focus-within:border-accent/40 focus-within:bg-foreground/[0.08]">
        <SearchIcon className="size-4 shrink-0 text-foreground/45" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('home.findPeoplePlaceholder')}
          className="min-h-11 min-w-0 flex-1 bg-transparent text-sm text-foreground placeholder:text-foreground/40 focus:outline-none"
          aria-label={t('home.findPeople')}
        />
      </label>
      {/* The shell is content-sized now, so the result list carries the bound
          itself — six rows, then it scrolls. */}
      <div className="mt-2 max-h-52 min-h-0 flex-1 overflow-y-auto">
        {/* Nothing until you type: the placeholder and the subtitle already
            say what goes in the box, and a third copy of the same sentence
            was the only thing filling this space. */}
        {q.trim() === '' ? null : loading ? (
          <p className="px-1 py-2 text-xs text-foreground/45">{t('home.findPeopleSearching')}</p>
        ) : results.length === 0 ? (
          <p className="px-1 py-2 text-xs text-foreground/45">{t('home.findPeopleEmpty')}</p>
        ) : (
          <ul className="flex flex-col gap-0.5">
            {results.slice(0, 6).map((u) => (
              <li key={u.id}>
                <button
                  type="button"
                  disabled={!!opening}
                  onClick={() => void openDm(u)}
                  className="flex w-full items-center gap-2.5 rounded-lg px-1.5 py-1.5 text-left transition-colors hover:bg-foreground/[0.07] disabled:opacity-60"
                >
                  <IdentityAvatar id={u.id} name={u.displayName || u.username} src={u.avatarUrl} size="sm" />
                  <span className="min-w-0">
                    <span className="block truncate text-sm text-foreground">{u.displayName || u.username}</span>
                    <span className="block truncate text-xs text-foreground/45">@{u.username}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}

/** Card 2 — start a group chat. */
function GroupCard() {
  const { t } = useTranslation();
  return (
    <Card title={t('home.createGroup')} subtitle={t('home.createGroupSub')}>
      {/* A LinkRow, like the Servers card next door. This was a paragraph of
          prose followed by a full-width secondary Button pinned to the bottom —
          a third way of offering an action, sitting between a search input and
          a list of link rows, so three neighbouring cards in one group each
          behaved differently. The prose went with it: the subtitle above
          already says what a group chat is, and repeating it in two sizes is
          how filler text gets written. */}
      <div className="mt-1 flex flex-col">
        <LinkRow
          icon={UsersIcon}
          label={t('home.createGroupCta')}
          onPress={() => emitAppEvent('groupdm:create')}
        />
      </div>
    </Card>
  );
}

/** Card 3 — create or join a server. */
function ServersCard() {
  const { t } = useTranslation();
  return (
    <Card title={t('home.servers')} subtitle={t('home.serversSub')}>
      <div className="mt-1 flex flex-col">
        <LinkRow icon={PlusIcon} label={t('home.createServer')} onPress={() => emitAppEvent('server:open', { tab: 'create' })} />
        <LinkRow icon={LinkIcon} label={t('home.joinServer')} onPress={() => emitAppEvent('server:open', { tab: 'join' })} />
      </div>
    </Card>
  );
}

/** Shared card shell — a heading and its action, without decorative icons. */
function Card(props: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <section
      className="studio-home-card flex min-h-0 flex-col rounded-2xl bg-foreground/[0.05] p-3 transition-colors hover:bg-foreground/[0.08] sm:min-h-[8.5rem] sm:p-3.5"
    >
      <div className="flex items-center">
        <h3 className="min-w-0 truncate text-sm font-semibold text-foreground">{props.title}</h3>
      </div>
      {props.subtitle && <p className="mt-0.5 text-xs leading-snug text-foreground/60">{props.subtitle}</p>}
      <div className="mt-2 flex min-h-0 flex-1 flex-col">{props.children}</div>
    </section>
  );
}

/** A ↗-style link row inside a card (servers create / join). */
function LinkRow(props: { icon: ComponentType<{ className?: string }>; label: string; onPress: () => void }) {
  const Icon = props.icon;
  return (
    <button
      type="button"
      onClick={props.onPress}
      className="group flex min-h-11 items-center gap-2.5 rounded-lg px-1.5 py-2 text-left transition-colors hover:bg-foreground/[0.07]"
    >
      <span className="grid size-7 shrink-0 place-items-center rounded-lg bg-foreground/[0.06] text-foreground/70 transition-colors group-hover:text-accent">
        <Icon className="size-4" />
      </span>
      <span className="text-sm text-foreground">{props.label}</span>
    </button>
  );
}
